import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, Tooltip, Legend,
} from "recharts";

interface DataPoint {
  week?: string;
  month?: string;
  ideas: number;
  teams: number;
}

interface Props {
  data: DataPoint[];
  timeframe?: 'weekly' | 'monthly';
}

export default function WeeklyTrendsChart({ data, timeframe = 'weekly' }: Props) {
  const dataKey = timeframe === 'monthly' ? 'month' : 'week';
  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        No activity yet
      </div>
    );
  }

  // Create an array for weeks 1-9
  const allWeeks = Array.from({ length: 9 }, (_, i) => ({
    week: `W${i + 1}`,
    ideas: 0,
    teams: 0,
  }));

  // Merge actual data with all weeks
  const displayData = allWeeks.map(week => {
    const actualData = data.find(d => d.week === week.week);
    return actualData ? { ...week, ideas: actualData.ideas, teams: actualData.teams } : week;
  });
  
  // Calculate dynamic width: 9 weeks
  const minWidth = 350;
  const widthPerWeek = 50;
  const dynamicWidth = Math.max(minWidth, displayData.length * widthPerWeek);

  return (
    <div className="w-full overflow-x-auto">
      <ResponsiveContainer width={dynamicWidth} height={300}>
        <LineChart 
          data={displayData} 
          margin={{ top: 5, right: 15, left: 35, bottom: 25 }}
        >
        <CartesianGrid stroke="#EDF0F5" vertical={true} horizontal={true} />
        <XAxis 
          dataKey="week" 
          stroke="#8B93A8"
          type="category"
          tick={{ fontSize: 12 }}
          tickSize={10}
          interval={0}
          label={{ value: `Weeks (W1 to W9)`, position: 'insideBottomRight', offset: -15, fontSize: 12 }}
        />
        <YAxis 
          stroke="#8B93A8" 
          domain={[0, "auto"]}
          type="number"
          tick={{ fontSize: 12 }}
          label={{ value: 'Count (1 unit = 1 team)', angle: -90, position: 'insideLeft', offset: 10, fontSize: 12 }}
          width={50}
        />
        <Tooltip 
          contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px' }}
          formatter={(value) => [`${value}`, '']}
          labelFormatter={(label) => `Week: ${label}`}
        />
        <Line
          type="monotone"
          dataKey="ideas"
          stroke="#0058AB"
          strokeWidth={3}
          dot={{ r: 6, fill: "#0058AB", strokeWidth: 2, stroke: "#fff" }}
          activeDot={{ r: 8, fill: "#0058AB" }}
          name="Ideas Submitted"
          isAnimationActive={true}
        />
        <Line
          type="monotone"
          dataKey="teams"
          stroke="#00D5D0"
          strokeWidth={3}
          dot={{ r: 6, fill: "#00D5D0", strokeWidth: 2, stroke: "#fff" }}
          activeDot={{ r: 8, fill: "#00D5D0" }}
          name="Teams Formed"
          isAnimationActive={true}
        />
      </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
